import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

/** Path (relative to `buildUrl`) for the coach activity push endpoint. */
export const COACH_ACTIVITY_ENDPOINT = '/coaches/me/activities';

/** Every activity `type` the family feed supports (mirrors the enum in the DTOs). */
export type ActivityEventType =
  | 'guardian_invited'
  | 'guardian_removed'
  | 'guardian_left'
  | 'invite_accepted'
  | 'invite_declined'
  | 'session_booked'
  | 'session_rescheduled'
  | 'session_cancelled'
  | 'package_purchased'
  | 'membership_renewed'
  | 'coach_note_added'
  | 'coach_up_level'
  | 'coach_down_level'
  | 'level_completed'
  | 'pin_earned'
  | 'kid_added'
  | 'kid_profile_updated'
  | 'kid_removed';

/**
 * Builds a deterministic idempotency key for `eventId` in the shape the backend
 * prefers: `entityType:entityId:action[:discriminator]`.
 *
 * Deterministic keys are naturally idempotent (a retried push reuses the same key
 * so the server can dedupe) and trace back to the entity that changed. Add a
 * `discriminator` (e.g. an ISO timestamp or level number) when the same entity +
 * action can legitimately recur.
 */
export function buildActivityEventId(
  entityType: string,
  entityId: string,
  action: ActivityEventType,
  discriminator?: string,
): string {
  return [entityType, entityId, action, discriminator].filter(Boolean).join(':');
}

/**
 * Body for POST /api/v1/coaches/me/activities (mirrors `PushCoachActivityDto`).
 * The coach (actor) and `coach_name`/`kid_name` are resolved server-side from the
 * bearer token + `playerId`, so `params` carries only the remaining template values.
 */
export type PushCoachActivity = {
  /** Coach-unique idempotency key. */
  eventId: string;
  /** What happened. */
  type: ActivityEventType;
  /** Our player id the event concerns. */
  playerId: string;
  /** Type-specific template values (e.g. `{ level_name: 'Green', pin_name: 'Chipping' }`). */
  params?: Record<string, string>;
};

/**
 * Pushes one coach-authored activity event into the family feed via
 * POST `/api/v1/coaches/me/activities`. Authenticated with the signed-in coach's
 * Firebase bearer token (attached by the `usKidsGolfService` auth interceptor).
 * Responds `202 Accepted` with no body.
 *
 * This is a fire-and-forget side effect, so it defaults to `skipServerErrorAlert`:
 * a failed push is logged/rejected but never raises the shared error popup over the
 * coach's actual action. Callers can override via `options`.
 */
export async function pushCoachActivity(
  data: PushCoachActivity,
  options?: RequestOptions,
): Promise<void> {
  await usKidsGolfService.post<void, PushCoachActivity>(
    buildUrl(COACH_ACTIVITY_ENDPOINT),
    data,
    { skipServerErrorAlert: true, ...options },
  );
}
